import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import { Middleware, SWRConfig, SWRResponse } from 'swr';

// This is a SWR middleware for keeping the data even if key changes.
export const laggy: Middleware = (useSWRNext) => {
  return (key, fetcher, config) => {
    // Use a ref to store previous returned data.
    const laggyDataRef = useRef<unknown>(undefined);

    // Actual SWR hook.
    const swr = useSWRNext(key, fetcher, config);

    useEffect(() => {
      // Update ref if data is not undefined.
      if (swr.data !== undefined) {
        laggyDataRef.current = swr.data;
      }
    }, [swr.data]);

    // Expose a method to clear the laggy data, if any.
    const resetLaggy = useCallback(() => {
      laggyDataRef.current = undefined;
    }, []);

    // Fallback to previous data if the current data is undefined.
    const dataOrLaggyData =
      swr.data === undefined ? laggyDataRef.current : swr.data;

    // Is it showing previous data?
    const isLagging =
      swr.data === undefined && laggyDataRef.current !== undefined;

    // Also add a `isLagging` field to SWR.
    return Object.assign({}, swr, {
      data: dataOrLaggyData,
      isLagging,
      resetLaggy,
    });
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface LaggySWRResponse<Data = any, Error = any>
  extends SWRResponse<Data, Error> {
  readonly isLagging: boolean;
  readonly resetLaggy: () => void;
}

const DEFAULT_FALLBACK = {};

export const FallbackProvider: React.FC<
  React.PropsWithChildren<{
    readonly fallback: Record<string, unknown>;
  }>
> = ({ children, fallback }) => {
  return (
    <SWRConfig
      value={{
        fallback: fallback ?? DEFAULT_FALLBACK,
      }}
    >
      {children}
    </SWRConfig>
  );
};

export type ImmutableSWRResponse<Data, Error> = Omit<
  SWRResponse<Data, Error>,
  'mutate'
>;

export type SWRResponseData<R extends SWRResponse> = R extends SWRResponse<
  infer D,
  unknown
>
  ? D
  : never;

export type SWRResponseError<R extends SWRResponse> = R extends SWRResponse<
  unknown,
  infer E
>
  ? E
  : never;

export function useMappedSWRResponse<D, E, O>(
  { data, ...response }: ImmutableSWRResponse<D, E>,
  apply: (data: D) => O,
  deps: readonly unknown[],
): ImmutableSWRResponse<O, E> {
  const newData = useMemo(
    () => (data !== undefined ? apply(data) : undefined),
    [data, apply, ...deps],
  );

  return {
    data: newData,
    ...response,
  };
}

export function combineSWRResponses<R extends Record<string, SWRResponse>>(
  responses: R,
): ImmutableSWRResponse<
  {
    [K in keyof R]: SWRResponseData<R[K]>;
  },
  SWRResponseError<R[keyof R]>
> {
  const combinedResponseRef = useRef<
    ImmutableSWRResponse<
      {
        [K in keyof R]: SWRResponseData<R[K]>;
      },
      SWRResponseError<R[keyof R]>
    >
  >({
    get data() {
      if (
        Object.values(responses).some((response) => response.data === undefined)
      ) {
        return undefined;
      }

      const combinedData = Object.fromEntries(
        Object.entries(responses).map(([key, response]) => [
          key,
          response.data,
        ]),
      );

      return combinedData as {
        [K in keyof R]: SWRResponseData<R[K]>;
      };
    },
    get error() {
      return Object.values(responses).find((response) => response.error)?.error;
    },
    get isValidating() {
      return Object.values(responses).some((response) => response.isValidating);
    },
    get isLoading() {
      return Object.values(responses).some((response) => response.isLoading);
    },
  });

  return combinedResponseRef.current;
}
